// ViewReceiptModalTenant.jsx
import React, { useState, useEffect } from 'react';
import { getReceiptCountTenantwise, getReceiptJsonDataSingle } from '../apis/services/pgApi';
import RentReceipt from './rentReceipt';

// ─── View Receipt Modal (Tenant Version) ─────────────────────────────────────────────
const ViewReceiptModalTenant = ({ isOpen, onClose, selectedPG }) => {
  // State for detailed receipts (direct API call with admissionId)
  const [detailedReceipts, setDetailedReceipts] = useState([]);
  const [loadingReceipts, setLoadingReceipts] = useState(false);
  const [receiptsError, setReceiptsError] = useState(null);

  // State for receipt view (second API call)
  const [activeReceiptData, setActiveReceiptData] = useState(null);
  const [loadingReceiptData, setLoadingReceiptData] = useState(false);
  const [receiptDataError, setReceiptDataError] = useState(null);

  // State for expanded view (which admission is expanded)
  const [expandedAdmission, setExpandedAdmission] = useState(null);

  // Fetch receipts when modal opens
  useEffect(() => {
    if (isOpen && selectedPG?.PGID && selectedPG?.AdmissionId) {
      fetchDetailedReceipts(selectedPG.AdmissionId);
    } else {
      // Reset when modal closes
      setDetailedReceipts([]);
      setActiveReceiptData(null);
      setExpandedAdmission(null);
      setReceiptsError(null);
    }
  }, [isOpen, selectedPG]);

  const fetchDetailedReceipts = async (admissionId) => {
    if (!selectedPG?.PGID || !admissionId) {
      setReceiptsError("No admission found for this tenant");
      return;
    }

    setLoadingReceipts(true);
    setReceiptsError(null);

    try {
      // Direct API call with admissionId - skip the first call
      const response = await getReceiptCountTenantwise(selectedPG.PGID, admissionId);
      console.log("Detailed receipts response:", response);

      if (response.success && response.data) {
        setDetailedReceipts(response.data.receipts || []);
        setExpandedAdmission(admissionId);
      } else {
        setReceiptsError(response.message || "Failed to fetch receipts");
        setDetailedReceipts([]);
      }
    } catch (error) {
      console.error("Error fetching detailed receipts:", error);
      setReceiptsError(error.message || "An error occurred while fetching receipts");
      setDetailedReceipts([]);
    } finally {
      setLoadingReceipts(false);
    }
  };

  const fetchReceiptData = async (receiptId) => {
    if (!receiptId) return;

    setLoadingReceiptData(true);
    setReceiptDataError(null);

    try {
      const response = await getReceiptJsonDataSingle(receiptId);
      console.log("Receipt data response:", response);

      if (response.success && response.data) {
        // Parse the JSON data from the response
        const rawData = response.data;

        // The data is in a column with a GUID name - extract the JSON string
        const jsonKey = Object.keys(rawData).find(key => key.startsWith('JSON_') || key === 'JSON_F52E2B61-18A1-11d1-B105-00805F49916B');
        let parsedData = null;

        if (jsonKey && rawData[jsonKey]) {
          try {
            const jsonString = rawData[jsonKey];
            parsedData = typeof jsonString === 'string' ? JSON.parse(jsonString) : jsonString;
          } catch (parseError) {
            console.error("Error parsing receipt JSON:", parseError);
            parsedData = rawData;
          }
        } else {
          parsedData = rawData;
        }

        setActiveReceiptData(parsedData);
      } else {
        setReceiptDataError(response.message || "Failed to fetch receipt data");
        alert(response.message || "Failed to fetch receipt data");
      }
    } catch (error) {
      console.error("Error fetching receipt data:", error);
      setReceiptDataError(error.message || "An error occurred while fetching receipt data");
      alert(error.message || "An error occurred while fetching receipt data");
    } finally {
      setLoadingReceiptData(false);
    }
  };

  const handleClose = () => {
    setActiveReceiptData(null);
    setDetailedReceipts([]);
    setExpandedAdmission(null);
    onClose();
  };

  if (!isOpen) return null;

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return "";
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (e) {
      return "";
    }
  };

  // Format date with time for display
  const formatDateTime = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return "";
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${day}-${month}-${year} ${hours}:${minutes}`;
    } catch (e) {
      return "";
    }
  };

  // Helper to get payment summary from receipt data
  const getPaymentSummary = (receiptData) => {
    if (!receiptData) return [];

    let data = receiptData;
    if (Array.isArray(data) && data.length > 0) {
      data = data[0];
    }

    if (data.PaymentSummary && Array.isArray(data.PaymentSummary)) {
      return data.PaymentSummary;
    }

    if (data.PaymentSummary && typeof data.PaymentSummary === 'object') {
      if (!Array.isArray(data.PaymentSummary)) {
        return [data.PaymentSummary];
      }
      return data.PaymentSummary;
    }

    const summary = [];
    if (data.TotalPaid) {
      summary.push({ ReferenceType: 'TOTAL', Amount: data.TotalPaid });
    }
    return summary;
  };

  // Helper to get header data from receipt
  const getReceiptHeader = (receiptData) => {
    if (!receiptData) return null;

    let data = receiptData;
    if (Array.isArray(data) && data.length > 0) {
      data = data[0];
    }

    if (data.Header && Array.isArray(data.Header) && data.Header.length > 0) {
      return data.Header[0];
    }

    if (data.ReceiptNo) {
      return data;
    }

    return null;
  };

  // Render the receipt view
  const renderReceipt = () => {
    if (!activeReceiptData) return null;

    const header = getReceiptHeader(activeReceiptData);
    if (!header) return null;

    const paymentSummary = getPaymentSummary(activeReceiptData);
    const totalAmount = header.TotalPaid || header.TotalAmount || 0;
    const amountFormatted = Number(totalAmount).toLocaleString('en-IN');

    return (
      <div className="rv-overlay rv-overlay-nested" onClick={(e) => e.stopPropagation()}>
        <div className="rcpt-receipt-wrap" onClick={(e) => e.stopPropagation()}>
          <RentReceipt
            pgName={header.PGName || selectedPG?.PGName || "Boys Town PG"}
            paidBy={header.TenantName || "Tenant"}
            amount={amountFormatted}
            paidOn={header.ReceiptDate ? formatDateTime(header.ReceiptDate) : ""}
            period={`${header.RentPeriod || "Current Month"}`}
            paymentMethod={header.PaymentMode || "CASH"}
            receiptNo={header.ReferenceNo || header.ReceiptNo || ""}
            receiptNumber={header.ReceiptNo || ""}
            receiptType="PAYMENT"
            roomNumber={header.RoomNumber || ""}
            bedNumber={header.BedNumber || ""}
            paymentSummary={paymentSummary}
            onClose={() => setActiveReceiptData(null)}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="rv-overlay" onClick={handleClose}>
      <div className="rv-modal rv-modal-lg" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="rv-head">
          <div>
            <h2 className="rv-title">View Receipt</h2>
            <p className="rv-sub">{selectedPG?.PGName ? `For ${selectedPG.PGName}` : "Your receipts"}</p>
          </div>
          <button className="rv-close" onClick={handleClose}>
            <svg width="15" height="15" viewBox="0 0 18 18" fill="none">
              <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="rv-body">
          {loadingReceipts ? (
            <div className="od-loading-state" style={{ padding: '40px' }}>
              <div className="od-spinner"></div>
              <p>Loading receipts...</p>
            </div>
          ) : receiptsError ? (
            <div className="od-error-state">
              <p>Error: {receiptsError}</p>
              <button 
                onClick={() => fetchDetailedReceipts(selectedPG?.AdmissionId)} 
                className="od-retry-btn"
              >
                Retry
              </button>
            </div>
          ) : detailedReceipts.length === 0 ? (
            <div className="od-empty-state" style={{ padding: '40px', textAlign: 'center' }}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
              </svg>
              <h3 className="od-empty-title">No receipts found</h3>
              <p className="od-empty-sub">No receipts have been generated for you yet.</p>
            </div>
          ) : (
            <div className="rcpt-accordion">
              {/* Show receipts in a simple list since we already have all data */}
              <div className="rcpt-accordion-item">
                <div className="rcpt-table">
                  <div className="rcpt-table-head">
                    <div className="rcpt-cell rcpt-cell-sno">S.No.</div>
                    <div className="rcpt-cell rcpt-cell-receiptno">Receipt No.</div>
                    <div className="rcpt-cell rcpt-cell-date">Receipt Date</div>
                    <div className="rcpt-cell rcpt-cell-mode">Payment Mode</div>
                    <div className="rcpt-cell rcpt-cell-amount">Amount</div>
                    <div className="rcpt-cell rcpt-cell-action">Action</div>
                  </div>
                  {detailedReceipts.map((receipt, idx) => (
                    <div className="rcpt-table-row" key={receipt.ReceiptID || idx}>
                      <div className="rcpt-cell rcpt-cell-sno">{idx + 1}</div>
                      <div className="rcpt-cell rcpt-cell-receiptno">{receipt.ReceiptNo}</div>
                      <div className="rcpt-cell rcpt-cell-date">{formatDate(receipt.ReceiptDate)}</div>
                      <div className="rcpt-cell rcpt-cell-mode">{receipt.PaymentMode}</div>
                      <div className="rcpt-cell rcpt-cell-amount">
                        ₹{Number(receipt.TotalAmount).toLocaleString('en-IN')}
                      </div>
                      <div className="rcpt-cell rcpt-cell-action">
                        <button
                          type="button"
                          className="od-vt-btn od-vt-btn-view"
                          onClick={() => fetchReceiptData(receipt.ReceiptID)}
                          disabled={loadingReceiptData}
                        >
                          {loadingReceiptData ? 'Loading...' : 'View Receipt'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Nested receipt view */}
      {activeReceiptData && renderReceipt()}
    </div>
  );
};

export default ViewReceiptModalTenant;